/**
 * Authentication API Service Module
 *
 * Handles user authentication including login, registration, token refresh, and logout.
 * All methods automatically include required headers (Authorization header is injected by axios client).
 *
 * @module api/auth
 * @example
 * ```typescript
 * import { authAPI } from '@/api';
 *
 * // Login
 * const { access_token, refresh_token, expires_in, user_id } = await authAPI.login({
 *   username: 'alice',
 *   password: 'Secret123!'
 * });
 *
 * // Get current user profile
 * const profile = await authAPI.me();
 * ```
 */

import { api } from './client';
import type {
  LoginRequest,
  RegisterRequest,
  RefreshTokenRequest,
  AuthResponse,
  UserProfile,
} from './types';

/**
 * Authentication API endpoints
 */
export const authAPI = {
  /**
   * POST /api/auth/login
   *
   * Authenticate user with username and password. Returns JWT token pair.
   *
   * @param request - Login credentials
   * @param request.username - User's username
   * @param request.password - User's password
   * @returns JWT token pair with user ID
   *
   * @example
   * ```typescript
   * const result = await authAPI.login({
   *   username: 'alice',
   *   password: 'Secret123!'
   * });
   *
   * // Returns:
   * // {
   * //   access_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
   * //   refresh_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
   * //   expires_in: 900,
   * //   token_type: "Bearer",
   * //   user_id: 42
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on authentication failure (invalid credentials)
   */
  login: async (request: LoginRequest): Promise<AuthResponse> =>
    api.post<AuthResponse>('/api/auth/login', request),

  walletChallenge: async (request: { address: string }) =>
    api.post<{ message: string; nonce: string; expires_in: number }>(
      '/api/auth/wallet-challenge',
      request,
    ),

  walletLogin: async (request: { address: string; message: string; signature: string }) =>
    api.post<AuthResponse>('/api/auth/wallet-login', request),

  /**
   * POST /api/auth/register
   *
   * Create a new user account. Requires a valid invite code from an existing member.
   *
   * @param request - Registration details
   * @param request.username - Desired username (must be unique)
   * @param request.password - Password (minimum requirements apply)
   * @param request.invite_code - Valid invite code from existing member
   * @returns JWT token pair with new user ID
   *
   * @example
   * ```typescript
   * const result = await authAPI.register({
   *   username: 'bob',
   *   password: 'Secret123!',
   *   invite_code: 'ABC123XYZ'
   * });
   *
   * // Returns:
   * // {
   * //   access_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
   * //   refresh_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
   * //   expires_in: 900,
   * //   token_type: "Bearer",
   * //   user_id: 43
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on validation failure (invalid invite code, username taken, weak password)
   */
  register: async (request: RegisterRequest): Promise<AuthResponse> =>
    api.post<AuthResponse>('/api/auth/register', request),

  /**
   * POST /api/auth/refresh
   *
   * Refresh the access token using a valid refresh token.
   * Typically called when access token expires (after expires_in seconds).
   *
   * @param request - Refresh token
   * @param request.refresh_token - Valid refresh token from login/register
   * @returns New JWT token pair
   *
   * @example
   * ```typescript
   * const refreshToken = localStorage.getItem('refresh_token');
   * const newTokens = await authAPI.refresh({
   *   refresh_token: refreshToken
   * });
   *
   * // Update stored tokens with new ones
   * ```
   *
   * @required_headers
   * - None (public endpoint, token is in request body)
   *
   * @throws ApiError if refresh token is invalid or expired
   */
  refresh: async (request: RefreshTokenRequest): Promise<AuthResponse> =>
    api.post<AuthResponse>('/api/auth/refresh', request),

  /**
   * GET /api/auth/me
   *
   * Fetch the current authenticated user's profile information.
   * Requires valid Bearer token in Authorization header (automatically injected).
   *
   * @returns Current user's profile data
   *
   * @example
   * ```typescript
   * const profile = await authAPI.me();
   *
   * // Returns:
   * // {
   * //   id: 42,
   * //   username: "alice",
   * //   email: "alice@example.com",
   * //   phone: "+1234567890",
   * //   invite_code: "ALICE_CODE",
   * //   referrer_id: null,
   * //   level_code: "VIP",
   * //   status: "active",
   * //   balances: {
   * //     uo: "1250.00",
   * //     ep: "500",
   * //     iep: "10"
   * //   },
   * //   create_time: "2024-01-15T10:30:00Z"
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  me: async (): Promise<UserProfile> =>
    api.get<UserProfile>('/api/auth/me'),

  /**
   * POST /api/auth/logout
   *
   * Invalidate the current session. The refresh token will no longer be valid.
   * Frontend should clear stored tokens after this call completes.
   *
   * @example
   * ```typescript
   * await authAPI.logout();
   * // Clear tokens from auth store and navigate to login
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid
   */
  logout: async (body?: { refresh_token?: string }): Promise<void> => {
    await api.post('/api/auth/logout', body ?? {});
  },

  changePassword: async (body: {
    old_password: string;
    new_password: string;
    confirm_password?: string;
    transaction_password?: string;
    security_answer?: string;
  }) => api.post<{ ok: true }>('/api/auth/change-password', body),

  changeSecurityAnswer: async (body: Record<string, string>) =>
    api.post<{ success?: boolean }>('/api/auth/change-security-answer', body),
};
